import React, { useEffect } from "react";
import { ComponentConfig } from "../../services/types/ComponentTypes";

interface MonthFilterProps {
    value?: string;
    onChange?: (month: string) => void;
    disabled?: boolean;
    filterField?: string;
    name?: string;
    formData?: Record<string, any>;
    onFormDataChange?: (name: string, value: any) => void;
    config?: ComponentConfig;
}

const MonthFilter: React.FC<MonthFilterProps> = ({
    value,
    onChange,
    disabled = false,
    filterField = "month",
    name,
    formData,
    onFormDataChange,
    config,
}) => {
    // Get value from formData if name is provided, otherwise use value prop
    const fieldName = name || (config as any)?.name;
    const currentValue = fieldName && formData ? formData[fieldName] : value;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // If onFormDataChange is provided and we have a field name, use it
        if (onFormDataChange && fieldName) {
            onFormDataChange(fieldName, newValue);
        }

        // Mirror to Json_Attribute (e.g. month_year) so module search payload matches summary column
        const jsonAttr = (config as any)?.Json_Attribute as string | undefined;
        if (onFormDataChange && jsonAttr && jsonAttr !== fieldName) {
            onFormDataChange(jsonAttr, newValue);
        }

        if (onFormDataChange && filterField === "payroll_month") {
            onFormDataChange("payroll_selected_employee_ids", []);
            onFormDataChange("payroll_stats_scope", "month");
        }

        // Also call onChange if provided (for backward compatibility)
        if (onChange) {
            onChange(newValue);
        }
    };

    const getCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        return `${year}-${month}`;
    };

    // Seed formData so list, stats, and Run Payroll share one month before user changes picker
    useEffect(() => {
        if (!onFormDataChange || !fieldName) return;
        const existing = String(currentValue || "").trim();
        if (existing) return;
        const ym = getCurrentMonth();
        onFormDataChange(fieldName, ym);
        const jsonAttr = (config as any)?.Json_Attribute as string | undefined;
        if (jsonAttr && jsonAttr !== fieldName) {
            onFormDataChange(jsonAttr, ym);
        }
    }, [fieldName, currentValue, onFormDataChange, config]);

    return (
        <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filter by {filterField}:
            </label>
            <input
                type="month"
                value={currentValue || getCurrentMonth()}
                onChange={handleChange}
                disabled={disabled || (config as any)?.disabled}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
            />
        </div>
    );
};

export default MonthFilter;
